import React from 'react';
import './MobileHedging.css';
import { formatPnL, getExtendedStats } from '../utils/stats';

function MobileHedging({ hedgingAnalysis }) {
  if (!hedgingAnalysis || hedgingAnalysis.length === 0) {
    return (
      <div className="mobile-hedging">
        <h1 className="mobile-header">Hedging Analysis</h1>
        <div className="no-hedging">No hedging data available</div>
      </div>
    );
  }

  // Get position value for visual bars
  const getPositionValue = (position) => {
    if (!position) return 0;
    return position.notional || (position.size * position.mark_price) || 0;
  };

  // Get all positions for a hedging analysis
  const getPositions = (analysis) => {
    const positions = [];
    if (analysis.binance_position) positions.push({ ...analysis.binance_position, exchange: 'Binance' });
    if (analysis.bybit_position) positions.push({ ...analysis.bybit_position, exchange: 'Bybit' });
    if (analysis.okx_position) positions.push({ ...analysis.okx_position, exchange: 'OKX' });
    if (analysis.hyperliquid_position) positions.push({ ...analysis.hyperliquid_position, exchange: 'Hyperliquid' });
    if (analysis.lighter_position) positions.push({ ...analysis.lighter_position, exchange: 'Lighter' });
    if (analysis.extended_position) positions.push({ ...analysis.extended_position, exchange: 'Extended' });
    return positions;
  };

  // Calculate max value for proportional bars
  const getMaxValue = (positions) => {
    if (positions.length === 0) return 1;
    return Math.max(...positions.map(p => getPositionValue(p)), 1);
  };

  // Calculate yield using funding-based calculation (same as desktop)
  const calculateYield = (analysis) => {
    const positions = getPositions(analysis);
    const stats = getExtendedStats(positions);
    // Use fundingPercentYearly from stats (based on actual funding payments, annualized)
    return stats?.fundingPercentYearly ?? 0;
  };

  return (
    <div className="mobile-hedging">
      <h1 className="mobile-header">Hedging Analysis</h1>

      <div className="hedging-cards">
        {hedgingAnalysis.map((analysis, index) => {
          const positions = getPositions(analysis);
          const maxValue = getMaxValue(positions);
          const yieldValue = calculateYield(analysis);

          // Determine status color
          const statusColor = 
            analysis.status === 'fully_hedged' ? '#10b981' :
            analysis.status === 'partially_hedged' ? '#f59e0b' :
            '#ef4444';

          const statusLabel = 
            analysis.status === 'fully_hedged' ? 'Fully Hedged' :
            analysis.status === 'partially_hedged' ? 'Partially Hedged' :
            'Unhedged';

          return (
            <div key={`${analysis.pair}-${index}`} className="hedging-card">
              <div className="hedging-header">
                <div className="hedging-pair">{analysis.pair}</div>
                <div className="status-badge" style={{ backgroundColor: `${statusColor}20`, color: statusColor, borderColor: statusColor }}>
                  {statusLabel}
                </div>
              </div>

              {/* Prominent Arbitrage Yield Display */}
              <div className={`arbitrage-yield-section ${yieldValue >= 0 ? 'positive' : 'negative'}`}>
                <div className="arbitrage-yield-label">Arb Yearly Yield</div>
                <div className={`arbitrage-yield-value ${yieldValue >= 0 ? 'positive' : 'negative'}`}>
                  {yieldValue >= 0 ? '+' : ''}{yieldValue.toFixed(2)}%
                </div>
              </div>

              {/* Position Breakdown */}
              <div className="position-breakdown">
                {positions.length === 2 ? (
                  // Two positions: thin bar, values above, exchange pills
                  (() => {
                    const totalValue = positions.reduce((sum, p) => sum + getPositionValue(p), 0);
                    return (
                      <div className="position-item unified-bar-item">
                        <div className="unified-bar-labels">
                          {positions.map((pos, posIndex) => (
                            <span key={posIndex} className="bar-label exchange-pill">
                              {pos.exchange}
                            </span>
                          ))}
                        </div>
                        <div className="bar-values-row">
                          {positions.map((pos, posIndex) => {
                            const value = getPositionValue(pos);
                            const pct = totalValue > 0 ? (value / totalValue) * 100 : 50;
                            return (
                              <div key={posIndex} className="bar-value-cell" style={{ width: `${pct}%` }}>
                                ${value.toFixed(0)}
                              </div>
                            );
                          })}
                        </div>
                        <div className="unified-bar-container unified-bar-thin">
                          {positions.map((pos, posIndex) => {
                            const value = getPositionValue(pos);
                            const barWidth = totalValue > 0 ? (value / totalValue) * 100 : 50;
                            const isLong = pos.side === 'LONG';
                            const barColor = isLong ? '#10b981' : '#ef4444';
                            return (
                              <div
                                key={posIndex}
                                className="unified-bar-segment"
                                style={{ width: `${barWidth}%`, backgroundColor: barColor }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  // Single position: same structure as two-position for aligned spacing and bar
                  positions.map((pos, posIndex) => {
                    const value = getPositionValue(pos);
                    const barWidth = maxValue > 0 ? (value / maxValue) * 100 : 0;
                    const isLong = pos.side === 'LONG';
                    const barColor = isLong ? '#10b981' : '#ef4444';

                    return (
                      <div key={posIndex} className="position-item unified-bar-item">
                        <div className="unified-bar-labels">
                          <span className="bar-label exchange-pill">{pos.exchange}:</span>
                        </div>
                        <div className="bar-values-row">
                          <div className="bar-value-cell bar-value-cell-single">${value.toFixed(2)}</div>
                        </div>
                        <div className="unified-bar-container unified-bar-thin">
                          <div
                            className="unified-bar-segment"
                            style={{ width: `${barWidth}%`, backgroundColor: barColor }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Metrics */}
              <div className="hedging-metrics">
                <div className="metric-item">
                  <div className="metric-label">Net Exposure</div>
                  <div className={`metric-value ${analysis.net_exposure >= 0 ? 'positive' : 'negative'}`}>
                    {formatPnL(analysis.net_exposure)}
                  </div>
                </div>
                <div className="metric-item">
                  <div className="metric-label">Hedge Ratio</div>
                  <div className="metric-value">
                    {(analysis.hedge_ratio * 100).toFixed(1)}%
                  </div>
                </div>
                <div className="metric-item">
                  <div className="metric-label">Agg. PnL</div>
                  <div className={`metric-value ${analysis.aggregated_pnl >= 0 ? 'positive' : 'negative'}`}>
                    {formatPnL(analysis.aggregated_pnl)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MobileHedging;

